import jsPDF from "jspdf";

export function generateApplicationForm(formData) {
  const doc = new jsPDF("p", "mm", "a4");
  const width = 210;
  const height = 297;

  const margin = 15;                 // outer border margin
  const innerPad = 8;                // inside padding
  const topOffset = 10;

  const pageLeft = margin;
  const pageRight = width - margin;
  const pageTop = margin;
  const pageBottom = height - margin;

  const contentLeft = pageLeft + innerPad;
  const contentRight = pageRight - innerPad;

  // ✅ a little safety so never touches border
  const contentWidth = (contentRight - contentLeft) - 2;

  const lineHeight = 6;              // compact to fit more
  const sectionGap = 4;

  let y = pageTop + topOffset;

  // ✅ SAFE VALUE PICKER (multiple key fallback)
  const pick = (...vals) => {
    for (let v of vals) {
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        return String(v);
      }
    }
    return "-";
  };

  const applicationNo =
    formData.application_no || `SAAM-${formData.dob?.split("-")[0] || "XXXX"}`;
  const membershipNo = formData.member_id || "APPLIED";

  const isNominal = String(formData.membership_type || "").toUpperCase() === "NOMINAL";
  const nominalFee = Number(formData.membership_fee || 1000);
  const coolingYears = Number(formData.cooling_period_years || 5);

  /* =========================================================
     ✅ TEXT SANITIZER
  ========================================================= */
  const decodeEntities = (s) => {
    let str = String(s ?? "");
    str = str
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&quot;/gi, '"')
      .replace(/&apos;/gi, "'")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">");
    str = str.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    );
    str = str.replace(/&#(\d+);/g, (_, num) =>
      String.fromCharCode(parseInt(num, 10))
    );
    return str;
  };

  const sanitizeText = (t) => {
    let s = decodeEntities(t);
    s = s.replace(/&(?=[A-Za-z0-9])/g, "");
    s = s.replace(/[\u200B-\u200D\uFEFF]/g, "");
    s = s.replace(/\u00A0/g, " ");
    s = s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "");
    s = s.replace(/\s+/g, " ").trim();
    return s || "-";
  };

  /* ✅ ONE FONT POLICY */
  const setFontNormal = (size = 10.5) => {
    doc.setFont("times", "normal");
    doc.setFontSize(size);
    doc.setTextColor(0, 0, 0);
    if (typeof doc.setCharSpace === "function") doc.setCharSpace(0);
  };
  const setFontBold = (size = 10.5) => {
    doc.setFont("times", "bold");
    doc.setFontSize(size);
    doc.setTextColor(0, 0, 0);
    if (typeof doc.setCharSpace === "function") doc.setCharSpace(0);
  };

  // ---------- styles ----------
  const labelStyle = () => {
    setFontNormal(10.5);
  };

  const valueStyle = () => {
    setFontBold(10.5);
  };

  const titleStyle = () => {
    setFontBold(11.5);
  };

  const smallStyle = () => {
    setFontNormal(10);
  };

  // ---------- helpers ----------
  const drawBorder = () => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1);
    doc.rect(pageLeft, pageTop, width - 2 * margin, height - 2 * margin);
  };

  const ensureSpace = (needed = lineHeight) => {
    const safeBottom = pageBottom - 12; // ✅ strict safe area
    if (y + needed > safeBottom) {
      doc.addPage();
      drawBorder();
      y = pageTop + topOffset;
      return true;
    }
    return false;
  };

  const hr = () => {
    ensureSpace(5);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);
    doc.line(contentLeft, y, contentRight, y);
    y += 5;
  };

  const addTextWrap = (text, x, yy, maxW) => {
    const safe = sanitizeText(text);
    const lines = doc.splitTextToSize(safe, Math.max(10, maxW));
    doc.text(lines, x, yy);
    return lines.length;
  };

  // 2-column field: label left, value right with wrapping
  const addField = (label, value) => {
    ensureSpace(lineHeight * 2);

    const labelW = 58;
    const valueX = contentLeft + labelW + 4;
    const valueW = contentRight - valueX - 2;

    labelStyle();
    doc.text(`${sanitizeText(label)}:`, contentLeft, y);

    valueStyle();
    const linesCount = addTextWrap(value, valueX, y, valueW);

    y += linesCount * lineHeight;
  };

  const addSectionTitle = (txt) => {
    ensureSpace(12);

    doc.setFillColor(245, 245, 245);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.rect(contentLeft, y - 4, contentWidth, 8, "F");

    titleStyle();
    doc.text(sanitizeText(txt), contentLeft + 2, y + 1);
    y += 10;
  };

  const addBulletLines = (lines) => {
    smallStyle();
    const bulletX = contentLeft + 2;
    const textX = contentLeft + 6;
    const maxW = (contentRight - textX) - 2;

    for (const t of lines) {
      const safe = sanitizeText(t);
      const wrapped = doc.splitTextToSize(safe, Math.max(10, maxW));
      ensureSpace(wrapped.length * lineHeight + 2);

      doc.text("•", bulletX, y);
      doc.text(wrapped, textX, y);

      y += wrapped.length * lineHeight;
    }
  };

  const formatDateDDMMYYYY = (d) => {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  };

  // ---------- header ----------
  const addHeader = () => {
    drawBorder();

    setFontBold(13);
    doc.text("SHINE AGRI ALLIED AND MARKETING", width / 2, y, { align: "center" });
    y += 6.5;

    setFontBold(12);
    doc.text("MULTISTATE COOPERATIVE SOCIETY LIMITED", width / 2, y, { align: "center" });
    y += 6;

    setFontNormal(10);
    doc.text("(Registered under Multi-State Cooperative Societies Act, 2002)", width / 2, y, {
      align: "center",
    });
    y += 5;

    setFontNormal(9.8);
    doc.text(
      "Head Office: 48, 1st Floor, Amar Arcade, Nilpur, Ta-Dantiwada, Banaskantha, Gujarat - 385505",
      width / 2,
      y,
      { align: "center", maxWidth: contentWidth }
    );
    y += 5;

    setFontNormal(9.8);
    doc.text("Mobile: 8490884020, 9313399225", width / 2, y, { align: "center" });

    y += 3;
    hr();

    setFontBold(11.5);
    const headingText = isNominal
      ? "APPLICATION FOR NOMINAL MEMBERSHIP"
      : "APPLICATION FOR MEMBERSHIP & SHARE ALLOTMENT";

    doc.text(headingText, width / 2, y, { align: "center" });
    y += 3;
    hr();

    const today = new Date();
    const formattedDate = formatDateDDMMYYYY(today);

    labelStyle();
    doc.text("Date:", contentLeft, y);
    valueStyle();
    doc.text(formattedDate, contentLeft + 15, y);

    labelStyle();
    doc.text("Application No.:", contentRight - 70, y);
    valueStyle();
    doc.text(sanitizeText(applicationNo), contentRight, y, { align: "right" });

    y += 8;

    // 🔻 IMPORTANT: aapne yaha pick() dubara paste kiya tha
    // ✅ delete nahi kiya, sirf comment kiya (warna confusion + future bugs)
    /*
    const pick = (...vals) => {
      for (let v of vals) {
        if (v !== undefined && v !== null && String(v).trim() !== "") {
          return String(v);
        }
      }
      return "-";
    };
    */
  };

  // ---------- body ----------
  const addBody = () => {
    ensureSpace(60);

    labelStyle();
    doc.text("To,", contentLeft, y); y += 6;
    doc.text("The Managing Director,", contentLeft, y); y += 6;
    doc.text("Shine Agri Allied and Marketing Multistate Cooperative Society Ltd.", contentLeft, y); y += 6;
    doc.text("Nilpur, Ta-Dantiwada, Dist-Banaskantha (Gujarat)", contentLeft, y); y += 8;

    setFontBold(10.8);
    const subjectText = isNominal
      ? "Subject: Application for Nominal Membership"
      : "Subject: Application for Membership and Share Allotment";
    doc.text(subjectText, contentLeft, y);
    y += 9;

    labelStyle();
    doc.text("Respected Sir/Madam,", contentLeft, y);
    y += 7;

    smallStyle();
    const feeText = `₹${nominalFee.toLocaleString("en-IN")}`;
    const p1 = isNominal
      ? `I, the undersigned, wish to apply for Nominal Membership in your esteemed Society. I understand that the Nominal Membership fee is ${feeText} and the cooling / lock-in period is ${coolingYears} years. Withdrawal request may be submitted after completion of the cooling period as per Society rules.`
      : "I, the undersigned, wish to apply for Membership in your esteemed Society and request allotment of shares as per applicable rules. My details are given below for your kind consideration.";

    const used1 = addTextWrap(p1, contentLeft, y, contentWidth);
    y += used1 * lineHeight + 4;

    addSectionTitle("A) Applicant Details");

    const cropsText = Array.isArray(formData.crops)
      ? formData.crops.join(", ")
      : pick(formData.crops);

    const memberData = [
      ["Membership No.", pick(membershipNo)],
      ["Farmer Category", pick(formData.farmer_category, formData.farmerCategory)],
      ["Full Name", pick(formData.name)],
      ["Father's Name", pick(formData.father_name, formData.fatherName)],
      ["Date of Birth", pick(formData.dob)],
      ["Gender", pick(formData.gender)],
      ["Address", pick(formData.address)],
      ["State", pick(formData.state)],
      ["District", pick(formData.district)],
      ["Taluka", pick(formData.taluka)],
      ["Village", pick(formData.village)],
      ["Mobile", pick(formData.mobile)],
      ["Email", pick(formData.email)],
      ["Occupation", pick(formData.occupation)],
      ["Aadhaar", pick(formData.aadhar)],
      ["PAN", pick(formData.pan)],
      ["Land Area (Hectare)", pick(formData.land_area_hectare, formData.land_area)],
      ["Khata No.", pick(formData.land_khata_no, formData.khata_no)],
      ["Survey No.", pick(formData.land_survey_no, formData.survey_no)],
      ["Selected Crops", pick(cropsText, Array.isArray(formData.crops) ? formData.crops.join(", ") : formData.crops)],
    ];

    for (let [label, value] of memberData) {
      ensureSpace(lineHeight * 2);
      addField(label, value);
    }

    addSectionTitle("B) Membership / Contribution Details");

    if (isNominal) {
      addField("Membership Type", "NOMINAL MEMBER (without shares)");
      addField("Nominal Membership Fee", `₹ ${nominalFee.toLocaleString("en-IN")}`);
      addField("Cooling / Lock-in Period", `${coolingYears} Years`);
      addField("Withdrawal", `Allowed after ${coolingYears} years (as per Society Rules)`);
      addField("Shares", "N/A (Nominal Member)");
      addField("Share Amount", "N/A");
    } else {
      addField("Membership Type", "REGULAR MEMBER (with shares)");
      addField("No. of Shares", pick(formData.shares));
      addField("Value Per Share", pick(formData.value_per_share));
      addField("Total Share Amount", pick(formData.total));
      addField("Land (in Bigha)", pick(formData.land));
    }

    addSectionTitle("C) Nominee Details");

    const nomineeData = [
      ["Nominee Name", pick(formData.nominee_name, formData.nomineeName)],
      ["Nominee Age", pick(formData.nominee_age, formData.nomineeAge)],
      ["Nominee Relation", pick(formData.nominee_relation, formData.nomineeRelation)],
      ["Nominee Address", pick(formData.nominee_address, formData.nomineeAddress)],
    ];

    for (let [label, value] of nomineeData) {
      addField(label, value);
    }

    addSectionTitle("D) Bank Details");

    const bankData = [
      ["Bank Name", pick(formData.bank_name)],
      ["Branch Name", pick(formData.bank_branch)],
      ["Account Holder Name", pick(formData.bank_account_holder)],
      ["Account No.", pick(formData.bank_account_no)],
      ["IFSC Code", pick(formData.bank_ifsc)],
      ["Account Type", pick(formData.bank_account_type)],
    ];
    for (let [label, value] of bankData) {
      addField(label, value);
    }
  };

  // ---------- declaration + terms + office use ----------
  const addDeclarationAndOfficeUse = () => {
    doc.addPage();
    drawBorder();
    y = pageTop + topOffset;

    addSectionTitle("Declaration");

    smallStyle();
    const dec1 =
      "I hereby declare that the information provided in this application is true and correct to the best of my knowledge and belief. I understand that providing false information may lead to rejection of the application and/or cancellation of membership as per Society rules.";
    y += addTextWrap(dec1, contentLeft, y, contentWidth) * lineHeight + 3;

    const dec2 =
      "I agree to abide by the Multi-State Cooperative Societies Act, 2002, the Rules, and the registered Bye-laws of the Society, and any amendments made from time to time.";
    y += addTextWrap(dec2, contentLeft, y, contentWidth) * lineHeight + 5;

    addSectionTitle("Terms & Conditions");

    const feeText = `₹${nominalFee.toLocaleString("en-IN")}`;
    const terms = isNominal
      ? [
          `Nominal Membership fee is ${feeText}.`,
          `Cooling / lock-in period is ${coolingYears} years. Withdrawal request may be submitted after completion of cooling period, subject to Society rules and approvals.`,
          "Nominal Members are not entitled to share capital allotment and voting rights (as per applicable rules/bye-laws).",
          "Membership benefits/services are provided as per Society policy and eligibility.",
          "All disputes, if any, shall be subject to applicable provisions of MSCS Act/Rules and Society bye-laws.",
        ]
      : [
          "Shares will be allotted as per Society rules/bye-laws after verification and approval.",
          "Membership rights and benefits are subject to eligibility and compliance with Society bye-laws.",
          "In case of any discrepancy or false information, membership may be rejected/cancelled as per rules.",
          "All disputes, if any, shall be subject to applicable provisions of MSCS Act/Rules and Society bye-laws.",
        ];
    addBulletLines(terms);

    y += 6;
    ensureSpace(30);

    labelStyle();
    doc.text("Place: ____________________", contentLeft, y);
    doc.text("Date: ____________________", contentRight - 55, y);
    y += 14;

    doc.text("Signature of Applicant: ________________________________", contentLeft, y);
    y += 14;

    hr();

    addSectionTitle("For Office Use Only");

    addField("Membership No.", pick(membershipNo));

    if (isNominal) {
      addField("Share Certificate No.", "N/A (Nominal Member)");
      addField("Number of Shares Allocated", "N/A");
    } else {
      addField("Share Certificate No.", pick(membershipNo));
      addField("Number of Shares Allocated", "____________");
    }

    addField("Board Resolution Date", "______________");
    addField("Approved / Rejected", "______________");
    addField("Remarks", "____________________________________________");

    y += 10;
    ensureSpace(25);

    setFontBold(10.5);
    doc.text("Authorized Signatory (With Seal & Signature)", contentLeft, y);
  };

  // ---------- run ----------
  addHeader();
  addBody();
  addDeclarationAndOfficeUse();

  doc.save(`${sanitizeText(membershipNo)}_ApplicationForm.pdf`);
}
